import React, { useEffect, useState } from "react";
import { FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { ago } from "@/lib/sk";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { WordDiff } from "@/components/canvas/WordDiff";

const solid = "inline-flex h-9 items-center gap-1.5 rounded-full bg-[hsl(var(--brand))] px-4 text-[13.5px] font-semibold text-white transition-colors hover:bg-[hsl(var(--brand-strong))] disabled:opacity-40";
const quiet = "inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13.5px] font-medium text-foreground/70 transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-40";

/** One line on the case: «‹rev2.pdf› ser ut som en ny versjon av spesifikasjonen – 3 nye, 2 endret, 1 ikke lenger med. Gjennomgå». */
export function RevisjonLine({ doc, onReview }) {
  const rv = doc.revision_review;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[14px] bg-[hsl(var(--brand-soft))] px-4 py-3" data-testid={`revisjon-line-${doc.id}`}>
      <FileText className="h-4 w-4 shrink-0 text-[hsl(var(--brand-strong))]" />
      <p className="min-w-0 flex-1 text-[15px] leading-[1.45] text-foreground"><span className="font-medium">«{doc.name}»</span> ser ut som en ny versjon av spesifikasjonen{rv.supersedes_name ? <span className="text-muted-foreground"> (erstatter «{rv.supersedes_name}»)</span> : null} – {rv.summary}. <span className="text-muted-foreground">Ingenting er endret ennå.</span></p>
      <button type="button" onClick={onReview} className={solid} data-testid={`revisjon-review-${doc.id}`}>Gjennomgå</button>
    </div>
  );
}

function Sec({ title, count, children, testId }) {
  return (
    <section data-testid={testId}>
      <h3 className="k-label">{title} · {count}</h3>
      <ul className="mt-1">{children}</ul>
    </section>
  );
}

const row = "border-t border-black/[0.06] py-3 first:border-t-0";

/**
 * The review of a newer specification against the posts on the case. New posts, changed wording (what exactly changed),
 * posts no longer asked for – and the one decision: take it in, or keep the file as an attachment. Our understanding,
 * lines and prices are never touched by either; taking it in is one undoable change.
 */
export function RevisjonSheet({ open, onOpenChange, project, docId, ops, onDone }) {
  const mobile = !useMediaQuery("(min-width: 640px)");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [working, setWorking] = useState(null);
  useEffect(() => {
    if (!open || !docId) return undefined;
    let alive = true;
    setData(null); setErr(null);
    api.get(`/projects/${project.id}/documents/${docId}/revisjon`).then((r) => alive && setData(r.data)).catch((e) => alive && setErr(errMsg(e)));
    return () => { alive = false; };
  }, [open, docId, project.id]);
  const decide = async (decision) => {
    setWorking(decision);
    try {
      const r = await api.post(`/projects/${project.id}/documents/${docId}/revisjon`, { decision });
      ops.apply?.(r.data);
      toast.success(decision === "godkjent" ? `Ny spesifikasjon tatt inn: ${data?.summary || "endringene er lagt inn"}` : "Filen ligger på saken som vedlegg – postene er uendret");
      onDone?.(decision, r.data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setWorking(null); }
  };
  const rv = data?.review;
  const c = rv?.counts || {};
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={mobile ? "bottom" : "right"} hideClose aria-describedby={undefined} className={cn("flex flex-col gap-0 p-0", mobile ? "h-[94dvh] rounded-t-[20px]" : "w-[min(100vw,820px)] sm:max-w-[820px]")} data-testid="revisjon-sheet">
        <SheetTitle className="sr-only">Ny versjon av spesifikasjonen</SheetTitle>
        <header className="flex items-start gap-3 border-b border-black/[0.06] px-6 pb-4 pt-5">
          <div className="min-w-0 flex-1">
            <div className="k-meta">Ny versjon av spesifikasjonen</div>
            <h2 className="mt-0.5 truncate text-[20px] font-semibold tracking-[-0.015em]" data-testid="revisjon-sheet-title">{data?.doc?.name || "…"}</h2>
            {rv ? <p className="k-meta mt-1">{rv.supersedes_name ? `Erstatter «${rv.supersedes_name}»` : ""}{rv.export_at ? ` · eksportert ${rv.export_at}` : ""}{data?.doc?.uploaded_at ? ` · lastet opp ${ago(data.doc.uploaded_at)}` : ""}</p> : null}
          </div>
          <button type="button" onClick={() => onOpenChange(false)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" aria-label="Lukk" data-testid="revisjon-sheet-close"><X className="h-4 w-4" /></button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 scrollbar-thin">
          {err ? <p className="text-[14px] text-[hsl(var(--v-blocked))]" data-testid="revisjon-sheet-error">{err}</p> : !data ? <div className="flex h-32 items-center justify-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div> : (
            <div className="space-y-8">
              <p className="text-[18px] font-medium leading-[1.4] tracking-[-0.01em]" data-testid="revisjon-sheet-summary">{data.summary.charAt(0).toUpperCase() + data.summary.slice(1)}{c.unchanged ? <span className="text-muted-foreground"> · {c.unchanged} uendret</span> : null}.</p>
              {rv.status !== "venter" ? <p className="k-meta" data-testid="revisjon-sheet-decided">Revisjonen er {rv.status}{rv.decided_by?.name ? ` av ${rv.decided_by.name}` : ""}{rv.decided_at ? ` · ${ago(rv.decided_at)}` : ""}.</p> : null}
              {rv.changed?.length ? (
                <Sec title="Endret ordlyd" count={rv.changed.length} testId="revisjon-changed">
                  {rv.changed.map((x) => (
                    <li key={x.item_id} className={row} data-testid={`revisjon-changed-${x.ref}`}>
                      <div className="flex items-baseline gap-3"><span className="k-ref w-[56px] shrink-0 text-muted-foreground">{x.ref}</span><span className="k-title min-w-0 flex-1 truncate">{x.title}</span>{x.touched ? <span className="k-meta shrink-0 !text-[hsl(var(--v-assumed))]">Skarpenord har vurdert denne</span> : null}</div>
                      <div className="mt-1.5 space-y-2 pl-[68px]">{x.fields.map((f) => <div key={f.field}><span className="k-meta">{f.label}</span><WordDiff before={f.before} after={f.after} className="mt-0.5" /></div>)}</div>
                    </li>
                  ))}
                </Sec>
              ) : null}
              {rv.added?.length ? (
                <Sec title="Nye poster" count={rv.added.length} testId="revisjon-added">
                  {rv.added.map((x) => (
                    <li key={x.ref} className={row} data-testid={`revisjon-added-${x.ref}`}>
                      <div className="flex items-baseline gap-3"><span className="k-ref w-[56px] shrink-0 text-muted-foreground">{x.ref}</span><span className="k-title min-w-0 flex-1 truncate">{x.title}</span>{x.group ? <span className="k-meta shrink-0">{x.group}</span> : null}</div>
                      {x.customer_text ? <p className="mt-1 line-clamp-3 pl-[68px] text-[14.5px] leading-[1.55] text-foreground/80">{x.customer_text}</p> : null}
                    </li>
                  ))}
                </Sec>
              ) : null}
              {rv.removed?.length ? (
                <Sec title="Ikke lenger med" count={rv.removed.length} testId="revisjon-removed">
                  {rv.removed.map((x) => (
                    <li key={x.item_id} className={row} data-testid={`revisjon-removed-${x.ref}`}>
                      <div className="flex items-baseline gap-3"><span className="k-ref w-[56px] shrink-0 text-muted-foreground">{x.ref}</span><span className="k-title min-w-0 flex-1 truncate text-foreground/70">{x.title}</span>{x.touched ? <span className="k-meta shrink-0 !text-[hsl(var(--v-assumed))]">Skarpenord har vurdert denne</span> : null}</div>
                      <p className="k-meta mt-1 pl-[68px]">Posten beholdes og merkes – du avgjør om den skal utgå.</p>
                    </li>
                  ))}
                </Sec>
              ) : null}
              {!rv.changed?.length && !rv.added?.length && !rv.removed?.length ? <p className="text-[15px] text-muted-foreground" data-testid="revisjon-none">Postene er de samme som i forrige spesifikasjon.</p> : null}
            </div>
          )}
        </div>
        {rv?.status === "venter" ? (
          <footer className="flex flex-wrap items-center gap-2 border-t border-black/[0.06] px-6 py-4" data-testid="revisjon-sheet-actions">
            <button type="button" onClick={() => decide("godkjent")} disabled={Boolean(working)} className={solid} data-testid="revisjon-accept">{working === "godkjent" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Ta inn endringene</button>
            <button type="button" onClick={() => decide("avvist")} disabled={Boolean(working)} className={quiet} data-testid="revisjon-decline">Behandle som vedlegg</button>
            <span className="k-meta ml-auto">Vår forståelse, linjer og priser røres ikke. Kan angres.</span>
          </footer>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
